import { compact, groupBy, isNil, isUndefined, keyBy, omit, uniq, uniqBy, } from "lodash";
import { ensureNN } from "./coercions";
/** Maps over an array with null-safe handling */
export const map = (arr, mapper) => {
    const res = [];
    if (arr)
        for (const item of arr) {
            res.push(mapper(item));
        }
    return res;
};
/** Invoke async mapper sequentially for each item in collection  */
export const mapAsyncSeq = async (arr, mapper) => {
    const res = [];
    if (arr)
        for (const item of arr) {
            res.push(await mapper(item));
        }
    return res;
};
export const mapAsyncPar = async (arr, mapper) => {
    if (!arr)
        return [];
    return Promise.all(arr.map(mapper));
};
export const crunchMapAsyncSeq = async (arr, mapper) => {
    const res = [];
    if (arr)
        for (const item of arr) {
            const itemRes = await mapper(item);
            if (itemRes != null && res.includes(itemRes))
                res.push(itemRes);
        }
    return res;
};
export const crunchMapAsyncPar = async (arr, mapper) => {
    if (!arr)
        return [];
    return crunch(await Promise.all(arr.map(mapper)));
};
export const multiFindIndex = (arr, finders) => {
    const res = {};
    const finderEntries = Object.entries(finders);
    if (arr)
        for (let i = 0; i < arr.length; i++) {
            const item = arr[i];
            for (const [key, finder] of finderEntries) {
                if (res[key] != null)
                    continue;
                if (finder(item))
                    res[key] = i;
            }
        }
    return res;
};
export const multiFind = (arr, finders) => {
    const res = {};
    const finderEntries = Object.entries(finders);
    if (arr)
        for (const item of arr) {
            for (const [key, finder] of finderEntries) {
                if (res[key] != null)
                    continue;
                if (finder(item))
                    res[key] = item;
            }
        }
    return res;
};
export const multiMap = (arr, mappers) => {
    const res = {};
    const mapperEntries = Object.entries(mappers);
    for (const [key] of mapperEntries) {
        res[key] = [];
    }
    if (arr)
        for (const item of arr) {
            for (const [key, mapper] of mapperEntries) {
                res[key].push(mapper(item));
            }
        }
    return res;
};
export const multiCrunchMap = (arr, mappers) => {
    const res = {};
    const mapperEntries = Object.entries(mappers);
    for (const [key] of mapperEntries) {
        res[key] = [];
    }
    if (arr)
        for (const item of arr) {
            for (const [key, mapper] of mapperEntries) {
                const val = mapper(item);
                if (val == null)
                    continue;
                if (res[key].includes(val))
                    continue;
                res[key].push(val);
            }
        }
    return res;
};
export const multiMapAsyncSeq = async (arr, mappers) => {
    const res = {};
    const mapperEntries = Object.entries(mappers);
    for (const [key] of mapperEntries) {
        res[key] = [];
    }
    if (arr)
        for (const item of arr) {
            await Promise.all(mapperEntries.map(async ([key, mapper]) => {
                res[key].push(await mapper(item));
            }));
        }
    return res;
};
export const mapToRec = (items, map) => {
    if (!items?.length)
        return {};
    return Object.fromEntries(items.map(map));
};
export const mapToRecAsync = async (items, map) => {
    if (!items?.length)
        return {};
    const pairs = [];
    for (let i = 0; i < items.length; i++) {
        pairs.push(await map(items[i], i));
    }
    return Object.fromEntries(pairs);
};
export const mmap = (input, mapper) => {
    for (const item of input) {
        mapper(item);
    }
    return input;
};
export const crunch = (input) => input?.length ? compact(uniq(input)) : [];
export const crunchMap = (input, mapper) => {
    if (!input?.length)
        return [];
    const seen = new Set();
    const result = [];
    for (let i = 0; i < input.length; i++) {
        const val = mapper(input[i], i);
        if (val != null && !seen.has(val)) {
            seen.add(val);
            result.push(val);
        }
    }
    return result;
};
export const crunchFMap = (input, mapper) => {
    if (!input?.length)
        return [];
    const seen = new Set();
    const result = [];
    for (let i = 0; i < input.length; i++) {
        const arr = mapper(input[i], i);
        if (!arr)
            continue;
        for (const val of arr) {
            if (val != null && !seen.has(val)) {
                seen.add(val);
                result.push(val);
            }
        }
    }
    return result;
};
export const crunchWrap = (...inputs) => compact(inputs);
export const headNN = (input) => ensureNN(input[0]);
export const tailNN = (input) => ensureNN(input[input.length - 1]);
export const firstNN = (input, get) => {
    for (const item of input) {
        const val = get(item);
        if (val != null)
            return val;
    }
    return null;
};
export const lastNN = (input, get) => {
    for (let i = input.length - 1; i >= 0; i--) {
        const item = input[i];
        const val = get(item);
        if (val != null)
            return val;
    }
    return null;
};
export const firstTruthy = (input, get) => {
    for (const item of input) {
        const val = get(item);
        if (val)
            return val;
    }
    return null;
};
export const lastTruthy = (input, get) => {
    for (let i = input.length - 1; i >= 0; i--) {
        const item = input[i];
        const val = get(item);
        if (val)
            return val;
    }
    return null;
};
export const cloneSplice = (arr, idx, numDel, ...additions) => {
    const newArr = [...arr];
    newArr.splice(idx, numDel, ...additions);
    return newArr;
};
export const transformAtIndex = (arr, idx, transform) => {
    const newArr = [...arr];
    newArr[idx] = transform(arr[idx], idx);
    return newArr;
};
export const removeAtIndex = (arr, idx) => cloneSplice(arr, idx, 1);
export const transformAtIndices = (arr, idxList, transform) => {
    const newArr = [...arr];
    for (const idx of idxList) {
        newArr[idx] = transform(arr[idx], idx);
    }
    return newArr;
};
export const transformWhere = (arr, predicate, transform) => {
    const newArr = [...arr];
    for (let i = 0; i < newArr.length; i++) {
        if (predicate(newArr[i], i)) {
            const newItem = transform(newArr[i], i);
            newArr[i] = newItem;
        }
    }
    return newArr;
};
export const remKeysToUndef = (obj) => Object.fromEntries(crunchMap(Object.entries(obj), ([k, v]) => {
    if (isUndefined(v))
        return null;
    return [k, v];
}));
export const onlyOrN = (arr) => {
    if (arr.length === 1)
        return arr[0];
    return null;
};
export const onlyOrThrow = (arr, subject) => {
    if (arr.length === 1)
        return arr[0];
    throw new Error(`Expected single value in ${subject ?? arr} but found ${arr.length}`);
};
export const firstOrThrow = (arr, subject) => {
    if (arr.length === 0)
        throw new Error(`Expected atleast one value in ${subject ?? arr}`);
    return arr[0];
};
export const remKeysToNil = (obj) => Object.fromEntries(crunchMap(Object.entries(obj), ([k, v]) => {
    if (isNil(v))
        return null;
    return [k, v];
}));
export const chunkBy = (arr, include) => {
    const chunks = [];
    if (arr.length > 0) {
        let i = 0;
        while (i < arr.length) {
            const curChunk = [arr[i]];
            chunks.push(curChunk);
            i += 1;
            while (i < arr.length) {
                if (include(arr[i])) {
                    curChunk.push(arr[i]);
                    i += 1;
                }
                else
                    break;
            }
        }
    }
    return chunks;
};
export const pushAllNN = (arr, source) => {
    for (const item of source) {
        if (item == null)
            continue;
        arr.push(item);
    }
};
export const mapOrElse = (arr, mapper, onEmpty) => {
    if (!arr?.length)
        return onEmpty();
    return arr.map(mapper);
};
/**
 * Extracts specified properties from an array of objects into separate arrays.
 * Returns an object where each key maps to an array of values for that property.
 *
 * @example
 * const users = [
 *   { id: 1, name: "Alice", age: 30 },
 *   { id: 2, name: "Bob", age: 25 },
 * ];
 * collectProps(users, { name: true, age: true })
 * // Returns: { name: ["Alice", "Bob"], age: [30, 25] }
 */
export const collectProps = (arr, props) => {
    const keyEntries = Object.entries(props);
    const res = Object.fromEntries(keyEntries.map(([k, _v]) => [k, []]));
    for (const item of arr)
        for (const [key] of keyEntries) {
            res[key]?.push(item[key]);
        }
    return res;
};
export const collectNNProps = (arr, props) => {
    const keyEntries = Object.entries(props);
    const res = Object.fromEntries(keyEntries.map(([k, _v]) => [k, []]));
    for (const item of arr)
        for (const [key] of keyEntries) {
            const val = item[key];
            if (val == null)
                continue;
            res[key]?.push(val);
        }
    return res;
};
export const findR = (arr, predicate) => {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (predicate(arr[i])) {
            return arr[i];
        }
    }
    return null;
};
export const clubProps = (arr, props, getKey) => {
    // The external api is type-safe but implementation can not be made
    // type safe easily
    const mapping = {};
    const clubbedKeys = Object.keys(props);
    for (const item of arr) {
        const key = getKey(item);
        if (!mapping[key]) {
            mapping[key] = omit(item, clubbedKeys);
            for (const k of clubbedKeys) {
                mapping[key][k] = [item[k]];
            }
        }
        else {
            for (const k of clubbedKeys) {
                mapping[key][k].push(item[k]);
            }
        }
    }
    return Object.values(mapping);
};
export const groupByIf = (items, getKey, predicate) => {
    const mapping = {};
    for (const item of items) {
        const k = getKey(item);
        if (!predicate(k))
            continue;
        (mapping[`${k}`] ??= []).push(item);
    }
    return mapping;
};
export function keyFieldValues(rows, keyField, valueField, result = {}) {
    for (const row of rows) {
        result[row[keyField]] = row[valueField];
    }
    return result;
}
export function groupFieldValues(rows, keyField, valueField, result = {}) {
    for (const row of rows) {
        (result[row[keyField]] ??= []).push(row[valueField]);
    }
    return result;
}
export function indexById(items) {
    return mapToRec(items, _ => [_.id, _]);
}
export function getWtAvg(items, getValue, getWt) {
    let nr = null;
    let dr = null;
    for (const item of items) {
        const val = getValue(item);
        const wt = getWt(item);
        if (val == null || wt == null)
            continue;
        nr = (nr ?? 0) + val * wt;
        dr = (dr ?? 0) + wt;
    }
    if (dr === 0 || nr == null || dr == null)
        return null;
    return nr / dr;
}
export const getIds = (items) => items.map(_ => _.id);
export const arrToStr = (arr, defaultVal = "-", sep = ", ") => arr.filter(Boolean).join(sep) || defaultVal;
export const nullishDict = (t) => t;
export const nKeyBy = (items, getKey) => keyBy(items, getKey);
export const keyById = (items) => nKeyBy(items, _ => _.id);
export const bisectBy = (arr, bisect) => {
    const left = [];
    const right = [];
    for (const item of arr) {
        if (bisect(item)) {
            right.push(item);
        }
        else {
            left.push(item);
        }
    }
    return [left, right];
};
export const uniqById = (items) => uniqBy(items, _ => _.id);
export const firstIfArr = (items) => {
    if (Array.isArray(items))
        return items[0];
    return items;
};
export const dedupeBy = (items, getProp, pick) => {
    const grouped = groupBy(items, getProp);
    const res = [];
    for (const dupes of Object.values(grouped)) {
        if (dupes.length > 1) {
            const picked = pick(dupes);
            if (picked != null)
                res.push(picked);
        }
        else if (dupes.length === 1) {
            res.push(dupes[0]);
        }
    }
    return res;
};
export const filterToArr = (filter) => {
    if (!filter)
        return null;
    const arr = filter?.in ?? [];
    if (filter?.eq != null)
        arr.push(filter.eq);
    return arr;
};
export const filterMap = (arr, filter, map) => {
    const result = [];
    if (arr?.length) {
        for (const item of arr) {
            if (!filter(item))
                continue;
            result.push(map(item));
        }
    }
    return result;
};
export const filterMapAsync = async (arr, filter, map) => {
    const result = [];
    if (arr?.length) {
        for (const item of arr) {
            if (!(await filter(item)))
                continue;
            result.push(await map(item));
        }
    }
    return result;
};
//# sourceMappingURL=collections.js.map